import { Injectable } from '@angular/core';
import { exchangeInitialState, EXCHANGE_STATUS_CONST, orderExchangeInitialState } from './const/exchange-orders';
import { Exchange, OrderExchange } from "@shared-logic/orders";
import { User } from '../../../shared-logic/user/user.interface';
import { OrderValidator } from "./order";
import { ExchangeService } from './services/exchange.service';
import { getBuyerExchange } from '@shared-logic/orders';
import { Order } from '@shared-logic/orders';
import { getProforma } from './dict/ibm/proformas';
import { WAY_PAY_VOID, WayPay, getExchangeWayPayFromProforma, getWayPayStruct } from '@shared-logic/payments';
import { SellerObject, sellerWithData } from './dict/seller-with-data';
import { formatNumber, parseFloatPoint } from './validator.checker';
import { SYNGENTA_NIDERA_AGREED_DISCOUNT } from '@shared-logic/subjects';
import { ToFixValidator } from './to-fix';
import { typeCoins, typeCoin, resetIndicatorsTransmission, Price } from '@shared-logic/prices';
import { EXCHANGER_STATES } from './dict/exchange';
import { ResponseRequest } from '@shared-logic/shared-models';
import { PURCHASE_METHOD_CONST } from '@shared-logic/purchase-method';

@Injectable({
  providedIn: 'root',
})
export class OrderExchangeValidator {

  constructor(private toFixV: ToFixValidator, private orderV: OrderValidator,
    private exchangeSvc: ExchangeService) { }

  /**
   * Returns true if the orderExchange has an incomplete field.
   * @param order The orderExchange to check for incompleteness.
   * @returns boolean
   */
  isOrderIncomplete(order: OrderExchange): boolean {
    return isExchangeIncomplete(order?.exchange) || this.orderV.isOrderIncomplete(order);
  }

  isExchangeIncomplete(exchange: Exchange) {
    return isExchangeIncomplete(exchange);
  }

  /**
   * Given an exchange, it returns true if the price is invalid.
   * The price is invalid if it is greater than 1000 and the type of coin is USD, or if it is less than 1000 and the type of coin is ARS.
   * @param exchange 
   * @returns boolean
   */
  isPriceExchangeInvalid(exchange: Exchange) {
    const price = exchange?.price ?? 0;
    const typeC = exchange?.typeCoin ?? '';
    return (price > 1000 && typeC == typeCoins.USD) || (price < 1000 && typeC == typeCoins.ARS)
  }

  /**
   * Save an order in the exchange database, completing some data
   * The third parameter is optional.
   * It is the mother order, which is to update the id of this created one in its idExchange.
   * @param order The order to save in the exchange database.
   * @param user The user who is saving the order.
   * @param orderMother The mother order to update the id of the created order.
   * */
  saveOrder(order: OrderExchange, user: User, orderMother?: Order) {
    let orderToSave: OrderExchange = JSON.parse(JSON.stringify(order));
    orderToSave.dataOfcreation = this.orderV.getDataOfCreation(user);
    orderToSave.exchange.status = EXCHANGE_STATUS_CONST.INCOMPLETO;
    this.exchangeSvc.createOrderExchange(orderToSave).subscribe((res: ResponseRequest) => {
      const idExchange = res.data;
      if (orderMother) {
        let copyOrderMother = JSON.parse(JSON.stringify(orderMother));
        copyOrderMother.idExchange = this.getIdExchange(copyOrderMother, idExchange);
        this.orderV.onlyUpdateOrder(copyOrderMother, user);
      }
    });
  }

  /**
   * Given an exchange, a user and an exchange order, 
   * if the exchange order does not exist, we set one with the initial values ​​and 
   * if it exists we set it with the previous values.
   * @param exchange 
   * @param user 
   * @param orderExchange 
   */
  saveExchange(exchange: Exchange, user: User, orderExchange?: OrderExchange) {
    let orderToSave = JSON.parse(JSON.stringify(!orderExchange ? orderExchangeInitialState : orderExchange));
    orderToSave.exchange = exchange;
    this.saveOrder(orderToSave, user);

    //Revisar bien si acepta orderExchange
  }

  /**
   * Given a redemption order structure, it edits it in the database, taking its id
   * @param order The order to edit in the database.
   * @param user The user who is editing the order.
   * */
  updateOrderExchange(order: OrderExchange) {
    this.exchangeSvc.updateOrderExchange(order, order._id).subscribe(res => { });
  }

  /**
   * Given an Exchange type structure, it returns true in case the status is Verify
   * @param exchange The exchange to check if the status is Verify.
   * @returns boolean - True if the status is Verify, false otherwise.
   * */
  isStatusVerify(exchange: Exchange) {
    return exchange.status == EXCHANGE_STATUS_CONST.VERIFICAR;
  }

  /**
   * Given an order and an Exchange, insert the exchange in the exchange field of the order
   * @param order The order to insert the exchange.
   * @param exchange The exchange to insert in the order.
   * @returns OrderExchange - The order with the exchange inserted.
   * */
  setExchangeInOrder(order: OrderExchange, exchange: Exchange) {
    let orderToReturn: OrderExchange = JSON.parse(JSON.stringify(order));
    orderToReturn.exchange = exchange;
    return orderToReturn;
  }

  /**
   * Given a swap order, obtain its _id and delete it from the DB
   * @param order The order to delete from the DB.
   */
  deleteExchange(order: OrderExchange) {
    this.exchangeSvc.deleteOrderExchange(order._id).subscribe(res => { });
  }

  // ------------- GETS ------------- \\
  getSellerOfExchange(order: OrderExchange) {
    const SELLERS: SellerObject = sellerWithData
    return SELLERS[order?.exchange?.buyer?.codeS]?.nombre ?? '--';
  }

  getExchangeStatus(order: OrderExchange) {
    const STATUS: any = EXCHANGER_STATES;
    return STATUS[order?.exchange?.status] ?? '--';
  }

  getContractNumber(order: OrderExchange) {
    return order?.exchange?.contractNumber > 0 ? order.exchange.contractNumber : '--';
  }

  getTypeCoin(order: OrderExchange) {
    const TYPE_COIN: any = typeCoin
    return TYPE_COIN[order?.exchange?.typeCoin] ?? '--';
  }

  getPrice(order: OrderExchange) {
    return order?.exchange?.price > 0 ? formatNumber(order.exchange.price) : '--';
  }

  getTons(order: OrderExchange) {
    return order?.exchange?.tons > 0 ? parseFloatPoint(order.exchange.tons) : '--';
  }

  /*getWayPay(order:OrderExchange){
    return getWayPayText(order.exchange.wayPay)
  }*/

  getBonification(exchange: Exchange) {
    return exchange?.amountB > 0 ? `${exchange.amountB} $`
      : exchange?.percentageB > 0 ? `${exchange.percentageB} %`
        : '--';
  }

  getIdExchange(order: OrderExchange, idExchange: string) {
    try {
      if (order.idExchange == '--' || !order.idExchange || order.idExchange == ' ') {
        return idExchange;
      } else if (order.idExchange.length > 0) {
        return order.idExchange + '/' + idExchange;
      }
    } catch (err) { }
    return idExchange;
  }

  getContraparts(idMother: string) {
    return this.exchangeSvc.getContraparts(idMother);
  }

  /**
   * Given a buyer code and a business type, returns the values that that business has.
   * N: Negocio a precio
   * S: Negocio a fijar
   * @param code - The buyer code to get the proforms.
   * @param isToPrice - Flag indicating if the price is to be fixed.
   * @returns The proforms of the buyer code and business type.
   * */
  getProforms(code: number, isToPrice: boolean) {
    let businessTypeString: string = isToPrice ? 'N' : 'S';
    return getProforma(code, businessTypeString);
  }

  /**
   * Get parent order from a swap order
   * @param {OrderExchange} order - The swap order from which to get the parent order.
   * @returns {OrderExchange} The parent order of the given swap order.
   **/
  getParentOrder(order: OrderExchange) {
    let copyOrder: OrderExchange = JSON.parse(JSON.stringify(order));
    copyOrder.sellerData = order.exchange.buyer;
    copyOrder.placeOfOrigin = order.exchange.placeOfOrigin;
    copyOrder.tons = order.exchange.tons;
    copyOrder.price.price = order.exchange.price;
    copyOrder.price.typeCoin = order.exchange.typeCoin;
    copyOrder.howSell = order.exchange.howSell;
    copyOrder.contractNumber = order.exchange.contractNumber;
    copyOrder.price.wayPay = getParentWayPay(order);
    copyOrder.idExchange = ''
    copyOrder.exchange = this.getExchangeInitial(copyOrder);
    copyOrder.exchange.isMotherCounterpart = 1;
    // Reset indicators: triple-band creates a new independent contract in IBM.
    // Inherited indicators must be re-sent with wasSent=false.
    resetIndicatorsTransmission(copyOrder.price);
    return copyOrder;
  }

  /**
   * Given an order, take the initial data to form an exchange consideration.
   * @param {Order} order - The order from which to form the initial exchange data.
   * @returns {Exchange} The initial exchange data formed from the order.
  */
  getExchangeInitial(order: Order): Exchange {
    const isToPrice = !this.toFixV.orderHaveIndicators(order);
    const codeB = getBuyerExchange(order);
    const getProforms = this.getProforms(codeB, isToPrice);
    const codeS = order.sellerData.codeS;
    const subSyng = order.price.businessParticularities.subdivisionSyngenta;
    const isNidera = this.orderV.isSyngentaNidera(codeS, subSyng);

    let newExchange: Exchange = JSON.parse(JSON.stringify(exchangeInitialState));
    newExchange.idMother = order._id; //Id de la orden.
    newExchange.tons = order.tons; //Toneladas de la orden.
    newExchange.placeOfOrigin = order.placeOfOrigin; //Lugar de entrega de la orden.
    newExchange.typeCoin = isToPrice ? order.price.typeCoin : '--'; //Tipo de moneda de la orden. 
    newExchange.agreedDiscount = isNidera ? SYNGENTA_NIDERA_AGREED_DISCOUNT : (getProforms?.agreedDiscount ?? 0);
    newExchange.priceDiscount = getProforms?.priceDiscount ?? 0;
    newExchange.purchaseMethod = PURCHASE_METHOD_CONST['PROPIEDAD'];
    // Payment method derived from the buyer proforma; never the mother one by default.
    newExchange.wayPay = getExchangeWayPayFromProforma(getProforms, order.price.wayPay, order.price);
    newExchange.price = isToPrice ? getNewPriceWithDiscounts(order.price.price, newExchange.priceDiscount) : 0; //Aplicamos priceDiscount al precio de la orden
    return newExchange;
  }

  /**
   * Exchange to save with a new mother order (order creation dialog). An exchange that was not completed
   * (to-price without price, or to-fix without exchange seller) is rebuilt from the mother defaults, but a
   * payment method loaded by the user (with its expiration) is kept, and the fixed-price mark is kept only
   * for a to-price mother. A completed exchange is returned as is.
   * @param {Order} mother - Mother order being created.
   * @param {Exchange} exchange - Exchange loaded in the form.
   * @returns {Exchange}
   */
  getExchangeForNewOrder(mother: Order, exchange: Exchange): Exchange {
    const isToPrice = !this.toFixV.orderHaveIndicators(mother);
    const isIncomplete = (isToPrice && exchange.price <= 0) || (!isToPrice && (exchange?.buyer?.codeS ?? 0) <= 0);
    if (!isIncomplete) {
      return exchange;
    }

    const rebuilt = this.getExchangeInitial(mother);
    if (exchange.wayPay?.wayPayName > 0) {
      rebuilt.wayPay = { ...exchange.wayPay };
    }
    rebuilt.isFixedPrice = isToPrice && exchange.isFixedPrice === true;
    return rebuilt;
  }

  /**
   * Builds an additional exchange for the same mother ("add another exchange" in the exchange slides).
   * Origin and purchase method come from the previous exchange; payment method, currency, discounts and price
   * are derived from the mother as for its first exchange. The mother link is left as in the initial state.
   * @param {Order} mother - Mother order of the exchanges.
   * @param {Exchange} previous - Exchange being completed before adding the new one.
   * @param {number} tons - Outstanding tons for the new exchange.
   * @returns {Exchange}
   */
  getAdditionalExchange(mother: Order, previous: Exchange, tons: number): Exchange {
    const derived = this.getExchangeInitial(mother);
    const newExchange: Exchange = JSON.parse(JSON.stringify(exchangeInitialState));
    newExchange.placeOfOrigin.province = previous.placeOfOrigin.province;
    newExchange.placeOfOrigin.town = previous.placeOfOrigin.town;
    newExchange.placeOfOrigin.afip = previous.placeOfOrigin.afip;
    newExchange.tons = tons;
    newExchange.purchaseMethod = PURCHASE_METHOD_CONST['PROPIEDAD'];
    newExchange.wayPay = derived.wayPay;
    newExchange.typeCoin = derived.typeCoin;
    newExchange.agreedDiscount = derived.agreedDiscount;
    newExchange.priceDiscount = derived.priceDiscount;
    newExchange.price = derived.price;
    return newExchange;
  }

  /**
  * Given an exchange, code of the seller, and a flag indicating if the price is to be fixed, 
  * it returns a copy of the exchange with updated proformas data. 
  * @param {Exchange} exchange - The exchange to update with proformas data.
  * @param {number} codeSeller - The seller code to get the buyer code.
  * @param {boolean} isToPrice - Flag indicating if the price is to be fixed.
  * @param {Price} motherPrice - Price of the mother contract, used when the proforma inherits its payment terms.
  * @returns {Exchange} A copy of the exchange with updated proformas data.
  */
  getProformsOfExchange(exchange: Exchange, codeSeller: number, isToPrice: boolean, motherPrice: Price) {
    let codeB = sellerWithData[codeSeller]?.buyerCode ?? 0;
    let copyExchange = JSON.parse(JSON.stringify(exchange));
    let getProforms = this.getProforms(codeB, isToPrice);
    //let agreedDiscount= getProforms? Number(getProforms.descConv.replace(',','.')): -1;
    let priceDiscount = getProforms?.priceDiscount ?? 0;
    const priceOfExchange = exchange.price > 0 && isToPrice ? exchange.price : 0;

    copyExchange.priceDiscount = priceDiscount > 0 ? priceDiscount : 0;
    // A payment method already loaded (derived when the buyer was chosen, or edited by the user) is kept;
    // it is only derived from the proforma when the exchange has none.
    copyExchange.wayPay = exchange.wayPay?.wayPayName > 0
      ? { ...exchange.wayPay }
      : getExchangeWayPayFromProforma(getProforms, motherPrice.wayPay, motherPrice);
    copyExchange.purchaseMethod = PURCHASE_METHOD_CONST['PROPIEDAD'];
    copyExchange.price = isToPrice ? getNewPriceWithDiscounts(priceOfExchange, copyExchange.priceDiscount) : -1
    return copyExchange;
  }

  /**
   * Given an exchange order, the selling commission of the exchange is returned.
   * @param {Order} order - The exchange order for which the commission is calculated.
   * @returns {Commission} The calculated selling commission for the given order.
   */
  getCommisionSellerInExchange(order: OrderExchange) {
    return this.orderV.getCommisionByOrder(JSON.parse(JSON.stringify(order)), 'V').seller;
  }
}

/**
 * Payment method of the exchange when its buyer (codeS) changes in the exchange form.
 * The first emission is the buyer already loaded, so the current payment method (possibly edited by the user)
 * is kept; any later buyer change re-derives it from the new buyer proforma.
 * @param {boolean} isFirstEmission - True for the buyer loaded when the form opens.
 * @param {WayPay} current - Current exchange payment method.
 * @param {WayPay} derived - Payment method derived from the proforma of the selected buyer.
 * @returns {WayPay}
 */
export function getExchangeWayPayOnBuyerChange(isFirstEmission: boolean, current: WayPay, derived: WayPay): WayPay {
  return isFirstEmission ? current : derived;
}

/**
 * Payment method of the new mother created from an exchange (triple band): the exchange payment method,
 * or for legacy exchanges the structure built from their codeWayPay. Without either, no payment method:
 * the previous mother payment method is never used as a default.
 * @param {OrderExchange} order - The exchange order.
 * @returns {WayPay} A new object, never shared with the order.
 */
export function getParentWayPay(order: OrderExchange): WayPay {
  if (order.exchange.wayPay?.wayPayName > 0) {
    return { ...order.exchange.wayPay };
  }
  if (order.exchange.codeWayPay > 0) {
    return { ...getWayPayStruct(order.exchange.codeWayPay, order.exchange.daysWayPay, order.price.wayPay) };
  }
  return { ...WAY_PAY_VOID };
}

/**
 * Given a price and a discount, it is applied if the discount is greater than 0
 * The priceDiscount is only applied to the price if and only if it is greater than 0
 * @param price business price
 * @param priceDiscount discount to apply
 * @returns 
 */
export function getNewPriceWithDiscounts(price: number, priceDiscount: number) {
  return priceDiscount > 0 ? price - (price * priceDiscount / 100) : price;
}

/**
 * Given an exchange struct, it returns true if it has an incomplete field.
 * Only 3 are analyzed because the others may be incomplete.
 * @param exchange 
 * @returns boolean
 */
export function isExchangeIncomplete(exchange: Exchange | null) {
  return !exchange ||
    exchange.placeOfOrigin.afip <= 0 ||
    exchange.howSell <= 0 ||
    exchange.buyer.codeS <= 0;
}

/**
 * Given an array of orders of exchanges. Performs a summation of all the values stored in each 'tons' field
 * @param orders Array of orders of exchanges
 * @returns The sum of all the values stored in each 'tons' field
 */
export function getSumOfTonsExchange(orders: OrderExchange[]) {
  let sum = 0;
  for (const order of orders) {
    sum += order.exchange.tons ?? 0;
  }
  return parseFloat(sum.toFixed(3)); // Retorna la sumatoria con un máximo de 3 decimales
}
